/**
 * VoiceOverlayPanel.tsx
 *
 * Panel-native renderer for the canonical Orbi dock. TWO layouts through ONE
 * shell and ONE orb (the canonical ChromeOrb sphere, item 2):
 *
 *   - ASK (default): compact panel. Neutral operator "Orbi" identity (item 1),
 *     a context chip, quick actions split into ask-actions (answer in-panel) and
 *     navigate-actions (carry an "opens [screen]" affordance, item 3), an inline
 *     "Call [subscriber]" affordance when a subject is primed (item 7), the chat
 *     log with cited answers, and a footer that sends free-text to
 *     orbi-operator-ask. The Talk control starts a voice call.
 *
 *   - VOICE (on Talk): the orb SCALES UP to a hero over a branded vertical-token
 *     gradient, with the live transcript BELOW it (item 5). Same orb, bigger.
 *
 * Icons are the canonical inline-SVG set, no emoji (item 6). Token only, no
 * em-dashes (Rule 7).
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChromeOrb } from './components/ChromeOrb';
import { MicIcon, HangupIcon, CloseIcon, SendIcon, PhoneIcon, NavigateIcon } from './components/Icons';
import { ConnectionState, type LogMessage, type OrbState, type AgentState } from './types';
import type { AskCitation } from './lib/operatorActions';
import { UiBlocks, type UiBlock, type UiAction } from './lib/uiRegistry';
import type { NavOutcome } from './lib/workspace';

interface CustomerPriming {
  id?: string;
  name?: string;
  phone?: string;
  account_number?: string;
  email?: string;
}

export interface QuickAction {
  key: string;
  label: string;
  hint?: string;
  disabled?: boolean;
  /** ask-actions answer in-panel; navigate-actions move the user to a screen. */
  kind: 'ask' | 'navigate';
  /** For navigate-actions, the screen they open (drives the affordance label). */
  opensLabel?: string;
  /** What to SAY to a LIVE agent when this starter is tapped mid-call. An
   *  ask-kind starter answers in the typed panel when there is no call; during
   *  a call the same question belongs in the conversation, spoken aloud, not
   *  written into a thread the operator is not looking at. Navigate-kind
   *  starters need none: their run() already goes in place mid-call. */
  voiceLine?: string;
  run: () => void;
}

export interface AskEntry {
  id: string;
  role: 'user' | 'orbi';
  text: string;
  citations?: AskCitation[];
  pending?: boolean;
  voice?: boolean;
  /* section-shaped nav comes from the model; route-shaped nav comes from the
     JARVIS J1 classifier (a config-resolved path like /v/isp/customers?...). */
  nav?: { client_slug?: string; section?: string; route?: string };
  /* Disambiguation: when a "call <name>" matches more than one subscriber, the
     orb renders one clickable Call button per candidate. */
  callOptions?: Array<{ name: string; account_number: string | null; phone: string | null; status?: string | null; building_slug?: string | null; is_test?: boolean; account_id?: string; reason?: string }>;
  /* Generative UI blocks (Phase 1), already registry-validated by the caller.
     Rendered through the UiBlocks registry renderer only, never as HTML. */
  ui?: UiBlock[];
}

/* A generative card the voice agent spins up via the show_card tool. The payload
   is AI-authored (heading / metric / description / badges / cta). */
export interface OrbiCardModel {
  id: string;
  type: string;
  payload: {
    intent?: string;
    heading?: string;
    metric?: string;
    description?: string;
    badges?: Array<{ text: string; variant?: string }>;
    cta?: { label: string; action: string };
    amount?: number;
    currency?: string;
  };
}

export interface VoiceOverlayPanelProps {
  connectionState: ConnectionState;
  agentState: AgentState;
  audioLevel: number;
  logs: LogMessage[];
  /** JARVIS MIC DEFECT FIX: human reason for the most recent failed voice
   *  start (mic permission denied, no device, Vapi/Daily join failure, or a
   *  hard timeout). Set alongside connectionState === ERROR; shown next to
   *  the mic control in ASK mode so a failed start is never silent. */
  micError?: string | null;
  sendMessage: (text: string) => void;
  stopCall: () => Promise<void> | void;
  identity: { title: string; subtitle?: string; scopeLabel?: string };
  customer?: CustomerPriming | null;
  showDemoFallback?: boolean;
  onClose: () => void;
  contextLine: string;
  primedChips: Array<{ label: string; title?: string }>;
  greetingName?: string | null;
  quickActions: QuickAction[];
  /** THE WORKSPACE'S WORD FOR A RECORD (D7a, 2026-09-18), singular and plural,
   *  from public.vertical_vocabulary through ORA.vocabulary(). The panel wrote
   *  the ISP's record noun at a clinic whose records are patients; this is the
   *  one noun it needs, and it defaults to the PLATFORM's vertical-neutral word
   *  rather than to the pilot's. */
  recordWord?: { one: string; many: string };
  /** What the starter list is: three offers that finish setting this workspace
   *  up, or three questions worth asking here. The heading differs because the
   *  two are different invitations. */
  startersKind?: 'setup' | 'ask';
  /** A file dropped on the panel. T5: a CSV dropped here used to do nothing at
   *  all, which is the one outcome a drop target may never have. */
  onFileDropped?: (file: { name: string; type: string }) => void;
  askLog: AskEntry[];
  askPending: boolean;
  onAsk: (text: string) => void;
  onStartVoice: () => void;
  /** SPEECH SYNC: the host router reports the ACTUAL outcome; mid-call tap
   *  messages to the agent are written from it, never from an assumption. */
  onNavigate: (nav: { client_slug?: string; section?: string; route?: string }) => NavOutcome | void;
  /** JARVIS NAV: when true the host router navigates IN PLACE during a live
   *  call (workspace layer - the call survives), so nav taps fire immediately
   *  instead of arming the deferred-to-call-end path. */
  inPlaceNav?: boolean;
  onCallOption?: (c: { name: string; account_number: string | null; phone: string | null; account_id?: string; reason?: string }) => void;
  /* Dispatches a validated generative-UI action (navigate / call / ask). */
  onUiAction?: (a: UiAction) => void;
  subjectName?: string;
  /* The primed subject's identity, in the same field order the call cards key on
     (account_number first — see OrbiDock's callCardKey and audit A5). Used ONLY to
     suppress a SECOND call affordance for a subject a card already offers. */
  subjectKey?: string | null;
  onCallSubject: () => void;
  callPending?: boolean;
  cards?: OrbiCardModel[];
  /* Registry blocks the voice agent spun up via show_blocks (Phase 4). Already
     validated by the caller; rendered in the voice stage through UiBlocks. */
  voiceUi?: UiBlock[];
  onDismissCard?: (id: string) => void;
  /* UX pass (Eri review item 5): a refresh starts a fresh chat; the prior
     thread stays reachable behind this subtle affordance. */
  prevCount?: number;
  onViewPrevious?: () => void;
}

/* Known platform sections, so a card CTA whose action names a section becomes a
   real navigation (the same path as "Take me there"). Forms accepted in the CTA
   action string: "network", "navigate:network", "open_customers", "slug/billing",
   "c/acme/discovery". Anything else is handed back to Orbi to continue the talk. */
const NAV_SECTIONS = ['dashboard', 'customers', 'network', 'appointments', 'costs', 'discovery', 'tickets', 'billing', 'resolve', 'settings'];
function parseNavAction(action?: string): { client_slug: string; section: string } | null {
  if (!action) return null;
  const cleaned = action.toLowerCase().trim().replace(/^(navigate|nav|open|go|goto|view|show)[\s:_-]+/, '');
  const segs = cleaned.split(/[\/:|>_-]/).map((s) => s.trim()).filter(Boolean);
  const section = segs.find((s) => NAV_SECTIONS.includes(s));
  if (!section) return null;
  const slug = segs.find((s) => s !== section && s !== 'c') || '';
  return { client_slug: slug, section };
}

function deriveOrbState(connection: ConnectionState, agent: AgentState): OrbState {
  if (connection === ConnectionState.ERROR) return 'error';
  if (connection === ConnectionState.DISCONNECTED) return 'dormant';
  if (connection === ConnectionState.CONNECTING) return 'thinking';
  switch (agent) {
    case 'listening':  return 'listening';
    case 'processing': return 'thinking';
    case 'speaking':   return 'responding';
    default:           return 'idle';
  }
}

export const VoiceOverlayPanel: React.FC<VoiceOverlayPanelProps> = ({
  connectionState,
  agentState,
  audioLevel,
  logs,
  micError,
  sendMessage,
  stopCall,
  identity,
  customer,
  onClose,
  contextLine,
  primedChips,
  greetingName,
  quickActions,
  recordWord,
  startersKind,
  onFileDropped,
  askLog,
  askPending,
  onAsk,
  onCallOption,
  onUiAction,
  onStartVoice,
  onNavigate,
  inPlaceNav,
  subjectName,
  subjectKey,
  onCallSubject,
  callPending,
  cards,
  voiceUi,
  onDismissCard,
  prevCount,
  onViewPrevious,
}) => {
  /* Phase 4: actions on voice-spun blocks. A navigate tap during a LIVE call
     never fires the host router directly (that would reload the page and drop
     the call); it arms the same deferred-nav used by card CTAs and fires the
     moment the call ends. Call and ask actions delegate to the normal gated
     dispatcher. */
  const onVoiceUiAction = (a: UiAction) => {
    if (a.action === 'navigate' && a.params.section && voiceActive) {
      const nav = { client_slug: a.params.client_slug || '', section: a.params.section };
      // JARVIS NAV: the host router navigates in place (the call survives), so
      // the tap fires now and the agent is told the screen is already opening.
      if (inPlaceNav) {
        // SPEECH SYNC: tell the agent what ACTUALLY happened, from the
        // router's outcome - opened in place, or did not (with the reason).
        const out = onNavigate(nav);
        try {
          sendMessage(out && out.moved === false
            ? `The operator tapped "${a.label}", but the ${nav.section} screen did NOT open: ${out.reason}. Say so honestly; do not claim it opened.`
            : `The operator tapped "${a.label}". The ${nav.section} screen is opening beside this call; carry on.`);
        } catch { /* no live channel */ }
        return;
      }
      const arming = !(pendingNav && pendingNav.section === nav.section);
      setPendingNav(arming ? nav : null);
      try {
        sendMessage(arming
          ? `The operator tapped "${a.label}". Briefly tell them you'll open ${nav.section} as soon as the call ends, then carry on.`
          : `The operator changed their mind about opening ${nav.section}; acknowledge briefly and carry on.`);
      } catch { /* no live channel */ }
      return;
    }
    if (onUiAction) onUiAction(a);
  };
  /* Card CTA. When the action names a section we navigate there - but NEVER while
     a call is live, because a host navigation reloads the page and drops the call.
     During a live call we defer: arm the target, let Orbi confirm out loud, and
     fire the navigation the instant the call ends. Tapping again disarms. */
  const onCardAction = (action: string, label: string) => {
    const nav = parseNavAction(action);
    if (nav) {
      // JARVIS NAV: in-place navigation keeps the call alive, so card CTAs
      // fire immediately mid-call instead of arming the deferred path.
      if (voiceActive && inPlaceNav) {
        // SPEECH SYNC: outcome-derived, same as the voice-block path above.
        const out = onNavigate(nav);
        try {
          sendMessage(out && out.moved === false
            ? `The operator tapped "${label}", but the ${nav.section} screen did NOT open: ${out.reason}. Say so honestly; do not claim it opened.`
            : `The operator tapped "${label}". The ${nav.section} screen is opening beside this call; carry on.`);
        } catch { /* no live channel */ }
        return;
      }
      if (voiceActive) {
        const arming = !(pendingNav && pendingNav.section === nav.section);
        setPendingNav(arming ? nav : null);
        try {
          sendMessage(arming
            ? `The operator tapped "${label}". Briefly tell them you'll open ${nav.section} as soon as the call ends, then carry on.`
            : `The operator changed their mind about opening ${nav.section}; acknowledge briefly and carry on.`);
        } catch { /* no live channel */ }
        return;
      }
      onNavigate(nav);
      return;
    }
    try { sendMessage(`The operator tapped "${label}".`); } catch { /* no live channel */ }
  };

  /* ONE renderer for the starter list, used by BOTH modes (see the voice
     starters note below). Tapping is what "they picked one" means, so the list
     stands itself down first, then acts:
       - during a LIVE call an ask-kind starter is SPOKEN to the agent, so the
         answer arrives in the conversation the operator is actually in rather
         than in the typed thread behind it;
       - a navigate-kind starter needs no special case - its run() already
         navigates in place mid-call and by document load otherwise;
       - with no call, run() is exactly what it always was.
     While a call is still connecting there is no channel to speak down, so
     ask-kind starters are held disabled rather than silently swallowed. */
  const runStarter = (a: QuickAction) => {
    if (voiceActive && a.kind === 'ask') {
      if (!isConnected || !a.voiceLine) return;
      setStartersTapped(true);
      try { sendMessage(a.voiceLine); } catch { /* no live channel: list stays down, nothing invented */ }
      return;
    }
    setStartersTapped(true);
    a.run();
  };

  /* "Show me" is right for three questions and wrong for three offers: an
     operator being shown what is left to set up is not asking to be shown
     anything. One list, one renderer, two honest headings. */
  const startersHeading = startersKind === 'setup' ? 'Next, to finish setting up' : 'Show me';

  const renderStarters = (variant: 'ask' | 'voice') => (
    <div className={`orbi-showme${variant === 'voice' ? ' orbi-showme-voice' : ''}`} data-starters-kind={startersKind || 'ask'}>
      <span className="orbi-showme-label">{startersHeading}</span>
      {quickActions.map((a) => (
        <button
          key={a.key}
          type="button"
          className={`orbi-showme-btn orbi-showme-${a.kind}`}
          onClick={() => runStarter(a)}
          disabled={a.disabled || (variant === 'voice' && a.kind === 'ask' && (!isConnected || !a.voiceLine))}
          title={a.hint || a.label}
        >
          <span className="orbi-showme-ic" aria-hidden="true">
            {a.kind === 'navigate' ? <NavigateIcon size={14} /> : <span className="orbi-showme-dot" />}
          </span>
          <span className="orbi-showme-text">{a.label}</span>
          {a.kind === 'navigate' && (
            <span className="orbi-showme-opens">opens {a.opensLabel || 'screen'}</span>
          )}
        </button>
      ))}
    </div>
  );

  const renderCards = () => {
    if (!cards || cards.length === 0) return null;
    const card = cards[cards.length - 1];
    const p = card.payload || {};
    const navTarget = parseNavAction(p.cta?.action);
    const deferred = !!(navTarget && voiceActive);
    const armed = !!(navTarget && pendingNav && pendingNav.section === navTarget.section);
    const sectionLabel = navTarget ? navTarget.section.charAt(0).toUpperCase() + navTarget.section.slice(1) : '';
    return (
      <div className="orbi-card-stack">
        <div className="orbi-card">
          {onDismissCard && (
            <button type="button" className="orbi-card-dismiss" aria-label="Dismiss card" onClick={() => onDismissCard(card.id)}>
              <CloseIcon size={12} />
            </button>
          )}
          {p.heading && <div className="orbi-card-heading">{p.heading}</div>}
          {p.metric && <div className="orbi-card-metric">{p.metric}</div>}
          {p.description && <div className="orbi-card-desc">{p.description}</div>}
          {p.badges && p.badges.length > 0 && (
            <div className="orbi-card-badges">
              {p.badges.map((b, i) => (
                <span key={i} className={`orbi-card-badge orbi-card-badge-${b.variant || 'muted'}`}>{b.text}</span>
              ))}
            </div>
          )}
          {p.cta && p.cta.label && (
            <button type="button" className="orbi-card-cta" onClick={() => onCardAction(p.cta!.action, p.cta!.label)}
              title={armed ? `Opens ${sectionLabel} when the call ends`
                   : deferred ? `Opens ${sectionLabel} after the call - your call keeps going`
                   : navTarget ? `Go to ${sectionLabel}` : p.cta.label}>
              <span>{armed ? `Opens ${sectionLabel} after the call` : p.cta.label}</span>
              {navTarget && <NavigateIcon size={13} />}
            </button>
          )}
        </div>
        {cards.length > 1 && (
          <div className="orbi-card-dots">
            {cards.map((c, i) => (
              <span key={c.id} className={`orbi-card-dot ${i === cards.length - 1 ? 'orbi-card-dot-active' : ''}`} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const scrollRef = useRef<HTMLDivElement | null>(null);
  /* JARVIS PANEL OVERLAY FIX: ask mode and voice mode used to be two mutually
     exclusive `return`s, so only one footer <input> ever existed at a time and
     one shared ref was safe. Both layers are now always mounted (see the
     merged return below), so each needs its own ref - one shared ref pointed
     at whichever layer rendered LAST would silently break focus/typing in the
     other. */
  const askInputRef = useRef<HTMLInputElement | null>(null);
  const voiceInputRef = useRef<HTMLInputElement | null>(null);
  const [input, setInput] = useState('');
  /* A card nav target armed during a live call, fired once the call ends so the
     operator lands on the page they asked for without the call ever dropping. */
  const [pendingNav, setPendingNav] = useState<{ client_slug: string; section: string } | null>(null);
  useEffect(() => {
    if (connectionState === ConnectionState.DISCONNECTED && pendingNav) {
      const target = pendingNav;
      setPendingNav(null);
      onNavigate(target);
    }
  }, [connectionState, pendingNav, onNavigate]);

  const isConnected  = connectionState === ConnectionState.CONNECTED;
  const isConnecting = connectionState === ConnectionState.CONNECTING;
  const isError      = connectionState === ConnectionState.ERROR;
  const voiceActive  = isConnected || isConnecting;

  const orbState = useMemo(
    () => deriveOrbState(connectionState, agentState),
    [connectionState, agentState]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [askLog.length, askPending, cards?.length]);

  useEffect(() => {
    if (!voiceActive) askInputRef.current?.focus();
  }, [voiceActive]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const text = input.trim();
      if (!text) return;
      if (voiceActive) {
        if (isConnected) sendMessage(text);
      } else {
        onAsk(text);
      }
      setInput('');
    },
    [input, voiceActive, isConnected, sendMessage, onAsk]
  );

  /* JARVIS MIC DEFECT FIX (rules-of-hooks): this useMemo used to live below the
     `if (voiceActive) return ...` early return further down, so it only ran on
     ASK-mode renders. The moment a call attempt flipped voiceActive from false
     to true (the mic click sets connectionState to CONNECTING synchronously in
     useVapi.startCall, before any stream is confirmed), this component called
     FEWER hooks than the previous render - a rules-of-hooks violation React
     detects as a hard error ("Rendered fewer hooks than expected"), which
     unmounts the panel. That is the actual mechanism behind "click the mic,
     the button disappears, nothing starts": the tree crashed, not just an
     unhelpful UI state. Every hook must run unconditionally on every render,
     so this moves above the early return with everything else. See also the
     fail-open error surfacing in useVapi.ts (micError) for the case where the
     call genuinely fails to connect rather than the render crashing. */
  const subjectOfferedByCard = useMemo(() => {
    const key = (subjectKey ?? '').trim();
    if (!key) return false;
    return askLog.some((m) =>
      (m.callOptions ?? []).some(
        (o) => o.account_number === key || o.account_id === key || o.phone === key,
      ),
    );
  }, [askLog, subjectKey]);

  const stateLabel = isConnecting ? 'Connecting' : isError ? 'Reconnecting' : agentState === 'speaking' ? 'Speaking' : agentState === 'processing' ? 'Thinking' : 'Listening';

  /* T1 (2026-09-18). This line named the ISP's record noun and the platform's
     own word for its own mechanism, on every workspace, including a clinic whose
     records are patients and whose operator had never used that noun for
     anything. Both were literals typed into this file, and the second one
     described a mechanism an operator has no reason to know about. The word is
     the workspace's now, and the sentence says the thing an operator can act on:
     nothing is open. */
  const recordOne = (recordWord?.one || 'Customer').trim();
  const customerTag = customer?.account_number
    ? `Account ${customer.account_number}`
    : customer?.phone
    ? customer.phone
    : `${recordOne.charAt(0).toUpperCase()}${recordOne.slice(1)} not open`;

  /* ── VOICE MODE: big central orb hero over the branded gradient. ONE focus line
     below it (item: the original ephemeral transcript pattern), not a chat log: the
     latest spoken line shows and is replaced by the next. When Orbi spins up a card
     the line yields and the card OWNS the space, exactly like the customer overlay. */
  const lastLine = logs.length > 0 ? logs[logs.length - 1] : null;
  const hasVoiceUi = !!(voiceUi && voiceUi.length > 0);
  const hasCard = !!(cards && cards.length > 0) || hasVoiceUi;

  /* ── CONVERSATION STARTERS IN VOICE (operator request 2026-08-27) ─────────
     The "Show me" list existed only in ASK mode. Hitting Talk dropped the
     operator onto a bare orb and the word "Listening for you" - the one moment
     they most need to know what this thing can be asked. The SAME list now
     opens a voice session too, and it is genuinely the same list: one
     quickActions array, one renderer (renderStarters below), so the two modes
     can never drift into offering different things.

     It gets out of the way the instant the conversation is real:
       - the caller speaks (any user turn in the transcript), or
       - they tap a starter,
     and a card or a registry block owns the stage outright. It never returns
     mid-call; a starter list reappearing over a live conversation would be
     the panel talking over the operator. */
  const [startersTapped, setStartersTapped] = useState(false);
  useEffect(() => { if (!voiceActive) setStartersTapped(false); }, [voiceActive]);
  const callerSpoke = useMemo(
    () => logs.some((l) => l.role === 'user' && !!(l.text || '').trim()),
    [logs]
  );
  const showVoiceStarters =
    voiceActive && !hasCard && !callerSpoke && !startersTapped && quickActions.length > 0;
  /* ── ASK MODE: branded primed hero (Eri 2026-06-24 restore). The hero leads with
     what Orbi is primed on (the on-screen chips), greets the operator, and names the
     lens scope + whether a subscriber is primed. Once a conversation starts the hero
     compacts (data-convo) and the chat takes over. ── */
  const hasConvo = askLog.length > 0 || askPending;

  /* JARVIS PANEL OVERLAY FIX (operator repro 2026-08-25): "click AUDIO, the
     whole slide-in panel disappears, but the call connects and audio keeps
     playing" - a healthy stream behind a vanished UI. Root cause: ask mode and
     voice mode used to be two separate `return`s selected by the `voiceActive`
     flag, so the INSTANT a call attempt flipped that flag, React tore down the
     entire ask-mode subtree (identity header, quick actions, chat log, footer)
     and mounted an entirely different voice-hero subtree in its place - a real
     unmount/remount of the chat container, not a swap within it. Any hiccup in
     that transition (and simply the transition itself, with nothing animating
     across it) read as the panel disappearing.
     Fix: ONE return, ONE outer shell, TWO always-mounted "layers" toggled by
     CSS (`data-mode` + `.orbi-mode-layer` in OrbiDock.css) instead of by
     conditional JSX branches. Starting or ending a call now overlays one layer
     over the other - neither ever unmounts - so the container the operator was
     looking at never vanishes, healthy stream or not. */
  return (
    <div
      className="orbi-panel orbi-panel-hero"
      data-state={orbState}
      data-mode={voiceActive ? 'voice' : 'ask'}
      /* Gated on !voiceActive so switching into voice mode can never leave a
         stale data-convo="true" behind: several CSS rules key off data-convo
         alone (not paired with data-mode), and both layers now share the DOM
         at once, so an ungated value here would leak ask-mode compacting
         styles onto the still-mounted voice layer. */
      data-convo={!voiceActive && hasConvo ? 'true' : 'false'}
      data-has-card={hasCard ? 'true' : 'false'}
      /* The voice stage is a centred one-line focus by default and cannot hold
         a list; when the starters are up it becomes a scrollable column and the
         orb steps down, the same accommodation data-has-card already makes. A
         data attribute rather than :has() so the rule is explicit and matches
         the pattern the rest of this panel already uses. */
      data-starters={showVoiceStarters ? 'true' : 'false'}
      /* T5 (2026-09-18): A CSV DROPPED HERE DID NOTHING. Not "was rejected" -
         nothing: this panel had no drop handler, so the browser's own default
         handled it, and dropping a file on an assistant that is plainly about
         this workspace's records looked like an import that silently failed.
         A drop target that swallows a file is the one outcome that is never
         acceptable, so the drop is INTERCEPTED and ANSWERED. The import itself
         is not wired from here on purpose: the import route belongs to the page
         the file is for (components/canonical/empty-state.js drives it, per
         page, through the manifest), and a second upload path in the assistant
         would be a second place for a failed import to be reported from.
         onDragOver must preventDefault as well, or the drop event never fires. */
      onDragOver={(e) => { if (onFileDropped && e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
      onDrop={(e) => {
        const f = e.dataTransfer?.files?.[0];
        if (!onFileDropped || !f) return;
        e.preventDefault();
        onFileDropped({ name: f.name, type: f.type || '' });
      }}
    >
      <button type="button" className="orbi-hero-close" aria-label="Close assistant panel" onClick={onClose}>
        <CloseIcon size={18} />
      </button>

      <div className="orbi-mode-layer orbi-mode-layer-voice">
        <div className="orbi-hero">
          <div className="orbi-hero-orb orbi-hero-orb-lg" aria-hidden="true">
            <ChromeOrb state={orbState} audioLevel={audioLevel} scaleFactor={1} />
          </div>
          <div className="orbi-hero-meta">
            {/* Just the live state word (the orb is Orbi; the scope/operator line was
                noise and is dropped). The priming label below carries the context. */}
            <span className="orbi-hero-state">{stateLabel}</span>
          </div>
          {/* Priming chips set expectations at call start, then get out of the way
              the moment Orbi starts talking so they never compete with the line/card.
              Same "Primed with whats on screen" label as the ask hero (standardised). */}
          {isConnecting && primedChips.length > 0 && (
            <div className="orbi-hero-primed orbi-hero-primed-voice">
              <span className="orbi-hero-primed-label">Primed with whats on screen</span>
              <div className="orbi-hero-chips orbi-hero-chips-voice">
                {primedChips.slice(0, 5).map((c, i) => (
                  <span key={i} className="orbi-hero-chip" title={c.title}>{c.label}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="orbi-stage orbi-stage-voice" role="log" aria-live="polite">
          {hasVoiceUi ? (
            /* Registry blocks spun up mid-call (Phase 4): same renderer, same
               validation as the typed ask path; navigate taps defer to call end. */
            <UiBlocks blocks={voiceUi} onAction={onVoiceUiAction} pending={callPending} />
          ) : hasCard ? (
            renderCards()
          ) : agentState === 'processing' ? (
            <div className="orbi-voice-thinking" aria-label="Thinking"><span /><span /><span /></div>
          ) : lastLine ? (
            <div key={lastLine.id} className={`orbi-voice-line orbi-voice-line-${lastLine.role}`}>{lastLine.text}</div>
          ) : (
            <div className="orbi-voice-line orbi-voice-line-idle">{isConnecting ? 'Connecting' : 'Listening for you'}</div>
          )}
          {showVoiceStarters && renderStarters('voice')}
        </div>

        <form className="orbi-panel-footer" onSubmit={handleSubmit}>
          <input
            ref={voiceInputRef}
            type="text"
            className="orbi-panel-input"
            placeholder={isConnected ? 'Type a message' : 'Connecting'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!isConnected}
            aria-label="Type a message"
          />
          <button
            type="button"
            className="orbi-panel-ctl orbi-panel-ctl-hangup"
            aria-label="Hang up"
            title="Hang up"
            onClick={() => void stopCall()}
          >
            <HangupIcon size={13} />
          </button>
        </form>
      </div>

      <div className="orbi-mode-layer orbi-mode-layer-ask">
        {/* Hero order is orb FIRST (Eri review item 6): the orb is pinned at the
            top on the panel's glass and keeps ONE stable size, so the collapsing
            greeting/chips below it never move or resize it. */}
        <div className="orbi-hero">
          <div className="orbi-hero-orb orbi-hero-orb-lg" aria-hidden="true">
            <ChromeOrb state="idle" audioLevel={0} scaleFactor={1} />
          </div>
          <div className="orbi-hero-greeting">
            {greetingName && <span className="orbi-hero-hi">Hi, {greetingName}</span>}
            <span className="orbi-hero-title">Ask me anything</span>
          </div>
          {primedChips.length > 0 && (
            <div className="orbi-hero-primed">
              <span className="orbi-hero-primed-label">Primed with whats on screen</span>
              <div className="orbi-hero-chips">
                {primedChips.map((c, i) => (
                  <span key={i} className="orbi-hero-chip" title={c.title}>{c.label}</span>
                ))}
              </div>
            </div>
          )}
          <div className="orbi-hero-meta">
            {identity.subtitle && <span className="orbi-hero-subtitle">{identity.subtitle}</span>}
            <span className="orbi-hero-customer">{customerTag}</span>
          </div>
        </div>

        <div ref={scrollRef} className="orbi-stage orbi-stage-ask" role="log" aria-live="polite">
          {/* A refresh parks the prior thread behind this quiet affordance (item 5);
              tapping it folds the old messages back in above the new chat. */}
          {(prevCount ?? 0) > 0 && onViewPrevious && (
            <button type="button" className="orbi-prev-chip" onClick={onViewPrevious}>
              View previous conversation ({prevCount})
            </button>
          )}
          {!hasConvo ? (
            renderStarters('ask')
          ) : (
            <>
              {askLog.map((m) => (
                <div key={m.id} className={`orbi-panel-msg orbi-panel-msg-${m.role === 'orbi' ? 'assistant' : 'user'}`}>
                  <span className="orbi-panel-msg-role">{m.role === 'orbi' ? 'assistant' : 'you'}</span>
                  <span className="orbi-panel-msg-text">{m.text}</span>
                  {/* Raw row-tag citation list intentionally not rendered: Orbi shows
                      only the synthesised answer, which carries a compact source line. */}
                  {m.nav && (
                    <button
                      type="button"
                      className="orbi-panel-nav-btn"
                      onClick={() => onNavigate(m.nav!)}
                      title={m.nav.route ? `Go to ${m.nav.route}` : `Go to ${m.nav.client_slug || 'all clients'} ${m.nav.section}`}
                    >
                      Take me there <NavigateIcon size={12} />
                    </button>
                  )}
                  {/* Generative UI blocks: registry-validated JSON rendered through
                      the UiBlocks registry only (Phase 1). */}
                  {m.ui && m.ui.length > 0 && onUiAction && (
                    <UiBlocks blocks={m.ui} onAction={onUiAction} pending={callPending || askPending} />
                  )}
                  {m.callOptions && m.callOptions.length > 0 && (
                    <div className="orbi-panel-call-cards">
                      {m.callOptions.map((c, i) => {
                        const sub = [c.account_number, c.status, c.building_slug].filter(Boolean).join(' · ');
                        const first = c.name.split(/\s+/)[0] || c.name;
                        return (
                          <div key={i} className="orbi-panel-call-card">
                            <div className="orbi-panel-call-top">
                              <span className="orbi-panel-call-name">{c.name}</span>
                              {c.is_test && <span className="orbi-panel-call-test">TEST</span>}
                            </div>
                            {sub && <div className="orbi-panel-call-sub">{sub}</div>}
                            {c.reason && <div className="orbi-panel-call-reason">Calling to {c.reason}</div>}
                            <button
                              type="button"
                              className="orbi-panel-call-btn"
                              onClick={() => onCallOption && onCallOption(c)}
                              title={`Your AI assistant places this call${c.account_number ? ` (${c.account_number})` : ''}; it connects only via the outbound allowlist`}
                            >
                              Let the AI call {first}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {askPending && (
                <div className="orbi-panel-msg orbi-panel-msg-assistant orbi-panel-msg-pending">
                  <span className="orbi-panel-msg-role">assistant</span>
                  <span className="orbi-panel-msg-text">Reading the rows on screen</span>
                </div>
              )}
            </>
          )}
          {/* Generative subject card + the gated Call affordance render at the
              BOTTOM of the flow as the newest entry (Eri review item 2), so the
              conversation always reads top to bottom. */}
          {cards && cards.length > 0 && renderCards()}
          {/* ONE PRIMARY PER INTENT (2026-08-19, go-live modal fault).
              The reported symptom was "Let the AI call Erika" rendered TWICE for one subject:
              once as a call card in the thread, once as this inline affordance underneath it.
              Both are real producers and both were correct on their own; together they offer
              the operator two buttons for one call, with no review of who or on what number.

              The card wins when it already offers this exact subject — it carries the account
              number, the status and the reason, so it is the more honest of the two — and this
              inline affordance stands down. When no card names the primed subject (the usual
              case: a drawer primed the dock and nothing has been asked yet) this is the only
              call affordance on screen, so it renders.

              Either way, whichever one the operator presses lands in the SAME governed flow:
              lib/resolve-governor.js intercepts orbi-operator-call and opens the shared
              Review-before-calling modal (lib/resolve-review.js). One review, one primary. */}
          {subjectName && !subjectOfferedByCard && (
            <button
              type="button"
              className="orbi-inline-call"
              onClick={onCallSubject}
              disabled={callPending}
              title={`Your AI assistant places this call (outbound, a ${recordOne.toLowerCase()} already on file, allowlist-gated)`}
            >
              <PhoneIcon size={16} />
              <span>Let the AI call {subjectName}</span>
            </button>
          )}
        </div>

        {/* JARVIS MIC DEFECT FIX: the mic control unmounts into the voice hero the
            instant a start is attempted (voiceActive below), so a failed start
            (permission denied, no device, Vapi/Daily join error, or the hard
            20s fail-open timeout in useVapi) used to fall silently back to this
            exact panel with zero explanation - the operator only saw the mic
            button flash away and reappear. Fail open: the control stays put,
            with the reason right beside it. */}
        {isError && micError && (
          <div className="orbi-mic-error" role="alert">{micError}</div>
        )}
        <form className="orbi-panel-footer" onSubmit={handleSubmit}>
          <input
            ref={askInputRef}
            type="text"
            className="orbi-panel-input"
            placeholder="Ask about anything you can see"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Ask the AI assistant"
          />
          <button
            type="submit"
            className="orbi-panel-ctl orbi-panel-ctl-send"
            aria-label="Send"
            title="Send"
          >
            <SendIcon size={18} />
          </button>
          <button
            type="button"
            className={`orbi-panel-ctl orbi-panel-ctl-mic${isError ? ' orbi-panel-ctl-mic-error' : ''}`}
            aria-label={isError ? 'Talk to the assistant (previous attempt failed, tap to retry)' : 'Talk to the assistant'}
            title={isError ? (micError || 'Previous attempt failed - tap to retry') : 'Talk to the assistant (starts a voice call)'}
            onClick={onStartVoice}
          >
            <MicIcon size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default VoiceOverlayPanel;
